import { useMemo } from 'react';
import { shuffle } from '@diritto/common/utils/array';
import { RelayType } from '@diritto/common/content/constants';
import { ContentRelayDto, ContentPublicRelayDto } from '@diritto/common/relay/dtos/content-relay.dtos';
import { ProfileLiteRelayDto, ProfileLitePublicRelayDto } from '@diritto/common/relay/dtos/profile-lite-relay.dtos';
import { EpisodeRelayDto } from '@diritto/common/relay/dtos/episode-relay.dtos';
import { EpisodeStatus } from '@diritto/common/episode/constants';
import { SectionRelayDto, SectionContentDto, SectionRankDto } from '@diritto/common/relay/dtos/section-relay.dtos';
import { SectionDesign } from '@diritto/common/home/constants/section-constants';

export const getValidContentAndProfile = (
  contentId: number,
  contents: Record<number, ContentRelayDto>,
  profileLites: Record<number, ProfileLiteRelayDto>,
  blockedProfileIds: Set<number>,
): { content: ContentPublicRelayDto; profile: ProfileLitePublicRelayDto } | null => {
  const content = contents[contentId];
  if (!content || content.relayType !== RelayType.Public) {
    return null;
  }

  const profile = content.profileId ? profileLites[content.profileId] : undefined;
  if (!profile || profile.isDeleted || blockedProfileIds.has(profile.id)) {
    return null;
  }

  return { content: content as ContentPublicRelayDto, profile: profile as ProfileLitePublicRelayDto };
};

export const getValidContentAndEpisode = (
  content: ContentRelayDto | undefined,
  episode: EpisodeRelayDto | undefined,
): { content: ContentPublicRelayDto; episode: EpisodeRelayDto } | null => {
  if (!content || content.relayType !== RelayType.Public) {
    return null;
  }

  if (!episode || episode.status !== EpisodeStatus.Public) {
    return null;
  }

  return { content: content as ContentPublicRelayDto, episode: episode as EpisodeRelayDto };
};

export const useRearrangedContentIds = (
  contentIds: number[],
  shufflePreviewItem: boolean,
  previewItemCount: number,
  sessionSeed: number,
) =>
  useMemo(() => {
    if (shufflePreviewItem) {
      return shuffle(contentIds, sessionSeed).slice(0, previewItemCount);
    }
    return contentIds.slice(0, previewItemCount);
  }, [contentIds, previewItemCount, shufflePreviewItem, sessionSeed]);

export const isSectionContentDto = (section: SectionRelayDto): section is SectionContentDto | SectionRankDto =>
  section.design === SectionDesign.Content ||
  section.design === SectionDesign.ContentLite ||
  section.design === SectionDesign.ContentCompact ||
  section.design === SectionDesign.Rank ||
  section.design === SectionDesign.RankLite;
